import React from "react";
import { selectors } from "@Src/redux/reducers";
import { useSelector, useDispatch } from "react-redux";
import { Creators } from "@Src/redux/actionTypes";
import { getQueryParams } from "@Src/utils/UrlUtility";
import { ROUTES, Member, DEALS_SSO_TOKEN_PARAMS } from "@Src/constants";
import { removeCookie } from "@Src/utils/cookie.utils";
import Loading from "@Components/loading";

const { getAuthenticationStatus } = selectors;
const readyToContinueStatuses = [
  Member.AUTHENTICATION_FAILURE,
  Member.AUTHENTICATION_SUCCESSFUL,
  Member.SIGN_IN_SUCCESSFUL,
];

const CheckForSession = ({ children }) => {
  const params = getQueryParams();
  const memberAuthStatus = useSelector(getAuthenticationStatus);
  const registrationType = useSelector(selectors.getRegistrationType);
  const [readyToContinue, setReadyToContinue] = React.useState(false);
  const isDirectorRoute = window.location.pathname === ROUTES.DIRECTOR_PATH;
  const dispatch = useDispatch();

  const isSsoRegType = registrationType === Member.DEALS_SSO;
  const hasCvt = !isSsoRegType && params.hasOwnProperty("cvt");

  const testForSsoTokens = React.useCallback(() => {
    if (!isSsoRegType) return false;
    const hasAllParams = DEALS_SSO_TOKEN_PARAMS.every((param) => {
      return params.hasOwnProperty(param);
    });
    return hasAllParams;
  }, [params, isSsoRegType]);

  const hasSsoToken = testForSsoTokens();

  // handles cvt auth, when needed
  const handleCvt = React.useCallback(() => {
    removeCookie(Member.CVT_SESSION);
    dispatch(Creators.authByCvt({ cvt: params.cvt }));
  }, [params, dispatch]);

  // handles sso auth, when needed
  const handleSso = React.useCallback(() => {
    removeCookie(Member.CVT_SESSION);
    const ssoTokenObj = {};
    DEALS_SSO_TOKEN_PARAMS.forEach((param) => {
      ssoTokenObj[param] = params[param];
    });
    dispatch(Creators.authBySsoToken(ssoTokenObj));
  }, [params, dispatch]);

  // checks for an existing/valid session
  const handleSessionAuth = React.useCallback(() => {
    dispatch(Creators.validateSession());
  }, [dispatch]);

  // handle cvt auth when present, or validate/check for existing session
  React.useEffect(() => {
    if (!readyToContinue && memberAuthStatus === null) {
      if (isDirectorRoute && hasCvt) {
        handleCvt();
      } else if (isDirectorRoute && hasSsoToken) {
        handleSso();
      } else if (!isDirectorRoute || (!hasCvt && !hasSsoToken)) {
        handleSessionAuth();
      }
    } else if (readyToContinueStatuses.includes(memberAuthStatus)) {
      setReadyToContinue(true);
    }
  }, [
    memberAuthStatus,
    isDirectorRoute,
    hasCvt,
    hasSsoToken,
    readyToContinue,
    handleCvt,
    handleSso,
    handleSessionAuth,
    setReadyToContinue,
  ]);

  if (!readyToContinue) {
    return <Loading />;
  }
  return children;
};

export default CheckForSession;
